import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import styles from './ClinicPatientConsults.module.scss';
import DashboardTableCard from '../components/DashboardTableCard';
import { Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import ModalForm from '../components/ModalForm';
import PatientHeader from '../components/PatientHeader';
import { NewConsultForm } from '../components/NewConsultForm';
import Button from '../components/Button';
import SubmitButton from '../components/SubmitButton';
import { alertModal, confirmModal } from '../utils/modalController';
import { fetchAuth } from '../utils/fetchAuth';
import { BsTrash } from 'react-icons/bs';

const apiUrl = process.env.REACT_APP_API_URL || '';
const TrashIcon = BsTrash as React.FC<{ size?: number; color?: string }>;

const ClinicPatientConsults: React.FC = () => {

    const [user, setUser] = useState<User | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [newConsult, setNewConsult] = useState<{ contrato_id: string; date: string } | null>(null);
    const [consultas, setConsultas] = useState<Consultas[]>([]);
    const [age, setAge] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();


    useEffect(() => {
        Promise.all([
            fetchAuth(apiUrl + `/consultorio/patients/${id}/consultations`),
            fetchAuth(`${apiUrl}/consultorio/patients/${id}`),
        ])
            .then(async ([consultsRes, patientRes]) => {
                if (!consultsRes.ok || !patientRes.ok) {
                    throw new Error('Erro ao buscar dados');
                }

                const consultsJson = await consultsRes.json();
                const patientJson = await patientRes.json();

                if (!patientJson.user) {
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${patientJson.user.id}`);

                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userJson = await userRes.json();

                setConsultas(consultsJson);
                setUser({
                    ...userJson,
                    patient: patientJson,
                });
                setAge(userJson?.birthday ? Math.floor((new Date().getTime() - new Date(userJson.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados:', err);
                alertModal('Erro ao buscar dados: ' + err);
                setConsultas([]);
                setUser(null);
                setAge(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, showModal]);

    if (loading) return <Loader />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente", href: `/consultorio/pacientes/${id}` },
                                        { label: "Consultas" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <DashboardTableCard
                welcomeMessage={`Consultas`}
                tableData={{
                    headings: ['Data', 'Ações'],
                    data: [
                        [<a href='#' className={styles["link"]} onClick={(e) => {
                            e.preventDefault();
                            setShowModal(true);
                            setNewConsult({ contrato_id: '', date: new Date().toISOString().slice(0, 16) });
                        }}>Nova consulta</a>],
                        ...consultas.map((consulta) => [
                            <Link to={`/consultorio/pacientes/${id}/consultas/${consulta.id}`} className={styles["link"]}>
                                {consulta.title}
                            </Link>,
                            <a href='#' className={styles["link"]} onClick={async (e) => {
                                e.preventDefault();
                                if (!await confirmModal('Tem certeza que deseja excluir esta consulta? Esta ação não pode ser desfeita.')) return;

                                try {
                                    const response = await fetchAuth(`${apiUrl}/consultorio/consultations/${consulta.id}`, {
                                        method: 'DELETE',
                                    });
                                    if (!response.ok) {
                                        const errorText = await response.text();
                                        throw new Error(`Erro na resposta da API: ${response.status} - ${errorText}`);
                                    }
                                    const json = await response.json();
                                    setConsultas(consultas.filter(c => c.id !== consulta.id));
                                } catch (err) {
                                    console.error('Erro ao excluir consulta:', err);
                                    alertModal('Erro ao excluir consulta: ' + err);
                                } finally {
                                }
                            }}>
                                <TrashIcon />
                            </a>
                        ])
                    ]

                }}
            />
            <ModalForm
                show={showModal}
                title="Nova Consulta"
                onHide={() => setShowModal(false)}
                onSubmit={async (e) => {
                    e.preventDefault();

                    if (saving) return;

                    setSaving(true);
                    fetchAuth(`${apiUrl}/consultorio/consultations`, {
                        method: 'POST',
                        body: JSON.stringify({
                            contrato_id: newConsult?.contrato_id,
                            data: newConsult?.date,
                        }),
                    })
                        .then(async (res) => {
                            if (!res.ok) {
                                const error_json = await res.json();
                                throw new Error(error_json.message || 'Erro ao salvar consulta');
                            }

                            const json = await res.json();

                            setShowModal(false);
                            navigate(`/consultorio/pacientes/${id}/consultas/${json.id}`);
                        }
                        )
                        .catch((err) => {
                            console.error('Erro ao salvar consulta:', err);
                            alertModal('Erro ao salvar consulta: ' + err);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button
                        key="cancel"
                        variant="secondary"
                        className={styles["modal-button"]}
                        onClick={() => setShowModal(false)}
                    >Cancelar</Button>,
                    <SubmitButton
                        key="submit"
                        type="submit"
                        className={styles["modal-button"]}
                        disabled={saving}
                    >{saving ? 'Salvando...' : 'Salvar'}</SubmitButton>
                ]}
            >
                <NewConsultForm
                    patient={user!}
                    onChange={(data) => {
                        setNewConsult(data);
                    }}
                />
            </ModalForm>
        </>
    );
};

export default ClinicPatientConsults;
